import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { adminAPI } from '../../services/api';
import { Save, Loader2, CreditCard, DollarSign, Settings as SettingsIcon, ShoppingCart } from 'lucide-react';

const AdminSettings = () => {
  const { isDarkMode } = useTheme();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminAPI.getSettings()
      .then((res) => setSettings(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const updateSetting = (key, value) => setSettings({ ...settings, [key]: value });

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {}
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const sections = [
    {
      title: 'Pagos - Stripe',
      icon: CreditCard,
      keys: ['stripe_enabled', 'stripe_publishable_key', 'stripe_secret_key', 'stripe_webhook_secret', 'stripe_fee_percentage', 'stripe_fixed_fee']
    },
    {
      title: 'Pagos - Billetera',
      icon: DollarSign,
      keys: ['wallet_enabled', 'wallet_fee_percentage']
    },
    {
      title: 'Pagos - PayPal',
      icon: CreditCard,
      keys: ['paypal_enabled', 'paypal_fee_percentage']
    },
    {
      title: 'Comisiones y Retiros',
      icon: SettingsIcon,
      keys: ['direct_commission_percentage', 'withdrawal_fee_percentage', 'min_withdrawal_amount', 'days_for_degradation']
    },
    {
      title: 'Tienda y Moneda',
      icon: ShoppingCart,
      keys: ['conversion_rate_usd_pen', 'company_name', 'company_email', 'default_shipping_time']
    }
  ];

  const settingLabels = {
    stripe_enabled: 'Habilitar Stripe',
    stripe_publishable_key: 'Publishable Key (test)',
    stripe_secret_key: 'Secret Key (test)',
    stripe_webhook_secret: 'Webhook Secret (test)',
    stripe_fee_percentage: 'Fee (%)',
    stripe_fixed_fee: 'Fee Fijo (USD)',
    wallet_enabled: 'Habilitar Billetera',
    wallet_fee_percentage: 'Fee Billetera (%)',
    paypal_enabled: 'Habilitar PayPal',
    paypal_fee_percentage: 'Fee PayPal (%)',
    direct_commission_percentage: 'Comisión Directa (%)',
    withdrawal_fee_percentage: 'Fee Retiro (%)',
    min_withdrawal_amount: 'Monto Mínimo Retiro (USD)',
    days_for_degradation: 'Días para Degradación',
    conversion_rate_usd_pen: 'Tasa USD a PEN',
    company_name: 'Nombre de Empresa',
    company_email: 'Email de Empresa',
    default_shipping_time: 'Tiempo de Envío',
  };

  const renderField = (key) => {
    const value = settings[key] || '';
    const label = settingLabels[key] || key;
    const isBool = key.includes('enabled');
    const isNumber = key.includes('percentage') || key.includes('fee') || key.includes('amount') || key.includes('days') || key.includes('rate');

    if (isBool) {
      return (
        <div key={key} className="flex items-center justify-between py-2">
          <span className="text-sm">{label}</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={value === 'true'} onChange={(e) => updateSetting(key, e.target.checked ? 'true' : 'false')} className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
          </label>
        </div>
      );
    }

    return (
      <div key={key} className="py-2">
        <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">{label}</label>
        <input
          type={isNumber ? 'number' : 'text'}
          step={key.includes('fee') ? '0.1' : '1'}
          value={value}
          onChange={(e) => updateSetting(key, e.target.value)}
          className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 text-sm"
        />
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Configuración</h1>
          <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Ajustes del sistema, pagos y tienda</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-xl hover:bg-indigo-700 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saved ? 'Guardado!' : 'Guardar Cambios'}
        </button>
      </div>

      {saved && (
        <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 p-4 rounded-xl text-sm">
          Configuración guardada exitosamente
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {sections.map((section) => (
          <div key={section.title} className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-xl">
                <section.icon className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-lg font-semibold">{section.title}</h2>
            </div>
            <div className="space-y-1">
              {section.keys.map((key) => renderField(key))}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default AdminSettings;
